// Defensive formatters for the planner row UI. Itinerary item JSONB columns
// (title, description, travellers, etc.) have inconsistent shapes across the
// dataset; every helper here walks the input shallowly and falls back to a
// readable default rather than throwing.

import type { DurationKind } from '@travel-planner/shared/utils';
import { UTCDate } from '@date-fns/utc';
import {
  durationKindOf,
  getDurationAndTravellers,
} from '@travel-planner/shared/utils';
import {
  readLegStartTimezone,
  resolveLegEndTimezone,
} from '@travel-planner/shared/utils/leg-timezone';
import { fromZonedTime } from 'date-fns-tz';
import { flightDurationMinutes } from '@/shared/lib/flight-duration';

const isLocalizedTextEntry = (
  v: unknown
): v is { languageCode: string; text: string } =>
  typeof v === 'object' &&
  v !== null &&
  'languageCode' in v &&
  'text' in v &&
  typeof (v as { text: unknown }).text === 'string';

export const resolveLocalizedText = (
  value: unknown,
  preferLanguage = 'en'
): string => {
  if (!Array.isArray(value)) return '';
  const preferred = value.find(
    (t): t is { languageCode: string; text: string } =>
      isLocalizedTextEntry(t) && t.languageCode === preferLanguage
  );
  if (preferred?.text) return preferred.text;
  const first = value.find(isLocalizedTextEntry);
  return first?.text ?? '';
};

// Wire contract: every timestamp is UTC wall-clock. `UTCDate` forces UTC
// parsing regardless of the viewer's browser TZ, matching legacy
// `formatToUTC(startTime, "HH:mm")`.
export const formatItemTime = (startTime: string | null): string => {
  if (!startTime) return '—';
  const d = new UTCDate(startTime);
  if (Number.isNaN(d.getTime())) return '—';
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
};

// Duration is rendered as a compact label. Default render:
//   "45m" / "1h" / "2h 30m" / "1d 6h".
// When the item is an accommodation, switches to "X nights" (matches the
// PDF/PPTX export). When the item is a rental car, switches to "X days"
// (also matches export). Items with no derivable duration render as null.
//
// Resolution order (mirrors shared `getDurationAndTravallers` + legacy):
//   1. Item kind → accommodation → `utcNights(start, end)` → "X nights".
//   2. Item kind → rental → `utcCalendarDaysInclusive(start, end)` → "X days".
//   3. Otherwise: explicit `startTime` AND `endTime` columns — diff in ms.
//   4. Fallback to `legs[0].details.startTime / .endTime` time-of-day diff
//      when only `startTime` is set (or both are missing). Activity and
//      transport products embed their canonical duration as a pair of
//      `{ hours, minutes }` objects on the first leg, so a freshly-added
//      item still surfaces a duration even before the consultant sets an
//      explicit end time.
//
// Callers that have an `ItineraryItem` should pass the item itself via the
// 4-arg shape — `formatDurationForItem(item)` — which derives the kind for
// you. The 3-arg `formatDuration(startTime, endTime, legs)` overload stays
// available for places that only have the raw columns.
export const formatDuration = (
  startTime: string | null,
  endTime: string | null,
  legs?: unknown,
  kind: DurationKind = 'other'
): string | null => {
  if (kind !== 'other' && startTime && endTime) {
    const { duration } = getDurationAndTravellers(
      { startTime, endTime, travellers: null },
      kind
    );
    return duration.length > 0 ? duration : null;
  }
  const { startTz, endTz } = legLocationTimezones(legs);
  const fromColumns = formatDurationFromMs(
    diffFromColumns(startTime, endTime, startTz, endTz)
  );
  if (fromColumns !== null) return fromColumns;
  return formatDurationFromMinutes(diffFromLegs(legs));
};

// Convenience overload for callers that have the full item — derives the
// kind via `durationKindOf` so accommodation / rental rows automatically
// render "X nights" / "X days" without each call site re-detecting.
//
// Flight rows get dedicated handling: their start/end date columns can drift
// several days apart (stale reschedules), so the generic column diff would
// report a multi-day span. We instead derive the true flight length from the
// leg's take-off/landing times — see `flightDurationMinutes`.
export const formatDurationForItem = (item: {
  startTime: string | null;
  endTime: string | null;
  legs?: unknown;
  contentCategory: string | null;
  contentSubcategory: string | null;
}): string | null => {
  if (item.contentSubcategory === 'Flight') {
    const minutes = flightDurationMinutes(item);
    if (minutes !== null) return formatDurationFromMinutes(minutes);
  }
  return formatDuration(
    item.startTime,
    item.endTime,
    item.legs,
    durationKindOf(item)
  );
};

// Start/end location IANA timezones from the first leg (when set). Used to
// turn the wall-clock start/end columns into true instants so the duration
// reflects timezone crossings (TP-3194 sibling: Africa/Lome 12:00 →
// Asia/Aqtau 21:00 must read 4h, not a naive 9h).
// An end location that names no place follows the start's zone.
const legLocationTimezones = (
  legs: unknown
): { startTz: string | null; endTz: string | null } => ({
  startTz: readLegStartTimezone(legs),
  endTz: resolveLegEndTimezone(legs),
});

// Wall-clock `yyyy-MM-ddTHH:mm:ss` of a UTC-wall-clock column, re-interpretable
// in a location timezone via `fromZonedTime`.
const wallClockOf = (iso: string): string | null => {
  const d = new UTCDate(iso);
  if (Number.isNaN(d.getTime())) return null;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}T${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:00`;
};

const diffFromColumns = (
  startTime: string | null,
  endTime: string | null,
  startTz?: string | null,
  endTz?: string | null
): number | null => {
  if (!startTime || !endTime) return null;
  // Timezone-aware when both endpoints carry a location timezone: the columns
  // are wall-clock, so re-interpret each side in its own zone to get the true
  // elapsed time. Falls back to the naive UTC diff on a missing/invalid zone.
  if (startTz && endTz) {
    const sw = wallClockOf(startTime);
    const ew = wallClockOf(endTime);
    if (sw && ew) {
      try {
        const ms =
          fromZonedTime(ew, endTz).getTime() -
          fromZonedTime(sw, startTz).getTime();
        if (Number.isFinite(ms)) return Math.max(0, ms);
      } catch {
        // Invalid timezone string — fall through to the naive diff below.
      }
    }
  }
  const a = new UTCDate(startTime);
  const b = new UTCDate(endTime);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  return Math.max(0, b.getTime() - a.getTime());
};

const isHM = (
  v: unknown
): v is { hours?: number | null; minutes?: number | null } =>
  typeof v === 'object' && v !== null;

const toMinutes = (
  hm: { hours?: number | null; minutes?: number | null } | null | undefined
): number | null => {
  if (!hm) return null;
  const h = typeof hm.hours === 'number' ? hm.hours : 0;
  const m = typeof hm.minutes === 'number' ? hm.minutes : 0;
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
};

const diffFromLegs = (legs: unknown): number | null => {
  if (!Array.isArray(legs) || legs.length === 0) return null;
  const first = legs[0] as { details?: unknown } | null;
  const details = first && typeof first === 'object' ? first.details : null;
  if (!details || typeof details !== 'object') return null;
  const d = details as { startTime?: unknown; endTime?: unknown };
  const startMin = toMinutes(isHM(d.startTime) ? d.startTime : null);
  const endMin = toMinutes(isHM(d.endTime) ? d.endTime : null);
  if (startMin == null || endMin == null) return null;
  // Accommodate "next day" wrap-arounds (e.g. 22:00 → 02:00 = 4h).
  const span =
    endMin >= startMin ? endMin - startMin : 24 * 60 - startMin + endMin;
  return span > 0 ? span : null;
};

const formatDurationFromMs = (ms: number | null): string | null => {
  if (ms == null || ms === 0) return null;
  return formatDurationFromMinutes(Math.round(ms / 60000));
};

const formatDurationFromMinutes = (
  totalMinutes: number | null
): string | null => {
  if (totalMinutes == null || totalMinutes <= 0) return null;
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes - days * 60 * 24) / 60);
  const minutes = totalMinutes - days * 60 * 24 - hours * 60;
  // Multi-day durations include minutes too (TP-3194): "1d 13h 45m".
  if (days > 0) {
    return [
      `${days}d`,
      hours > 0 ? `${hours}h` : '',
      minutes > 0 ? `${minutes}m` : '',
    ]
      .filter(Boolean)
      .join(' ');
  }
  if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  return `${minutes}m`;
};

export const formatLocation = (
  city: string | null,
  country: string | null
): string => {
  const c = city?.trim() ?? '';
  const k = country?.trim() ?? '';
  if (c && k) return `${c} · ${k}`;
  return c || k || '—';
};
